import Checkbox from '@allesauto/core/checkbox';
import SearchDropdown, { type Option } from '@allesauto/core/searchDropdown';
import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { DropdownOpenHeight } from '@allesauto/utils/consts';
import { getSlug } from '@allesauto/utils/funcs';
import React, { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { EquipmentFilterStateProps } from '@/store/redux/actions/pages/pcp';

export interface EquipmentFilterProps {
  className?: string;
  equipments: SchemaTypes.Equipment;
  onChange: (equiment: EquipmentFilterStateProps) => void;
  selected: EquipmentFilterStateProps;
}

const transformListToSearchDropdown = (
  values:
    | SchemaTypes.Equipment['comfort']
    | SchemaTypes.Equipment['entertainment']
    | SchemaTypes.Equipment['security']
): Option[] => {
  const searchDropdownList =
    values &&
    (values.map((value) =>
      value?.name && value.displayName
        ? {
            value: value.name,
            label: value.displayName,
          }
        : null
    ) as Option[]);
  return searchDropdownList || [];
};

const EquipmentFilter: FC<React.PropsWithChildren<EquipmentFilterProps>> = ({
  equipments,
  onChange,
  selected,
}) => {
  const intl = useIntl();
  const trackingIdentifierEquipment = 'aa-filter--equipment';
  const trackingIdentifierEquipmentAircon = `${trackingIdentifierEquipment}-aircon`;
  const trackingIdentifierEquipmentComfort = `${trackingIdentifierEquipment}-comfort`;
  const trackingIdentifierEquipmentSecurity = `${trackingIdentifierEquipment}-security`;
  const trackingIdentifierEquipmentEntertainment = `${trackingIdentifierEquipment}-entertainment`;

  const className = {
    subTitleCheckbox: 'text-white pb-30px',
    subTitleDropdown: 'text-white pb-10px',
    elementContentDropdown: 'mb-20px',
    elementContentCheckbox: 'mb-20px grid',
    airconItem: 'pb-20px',
  };
  const { aircon, comfort, entertainment, security } = equipments;
  const headlinePosition = 'top';

  const comfortForSearchDropdown: Option[] =
    transformListToSearchDropdown(comfort);
  const entertainmentForSerachDropdown: Option[] =
    transformListToSearchDropdown(entertainment);
  const securityForSerachDropdown: Option[] =
    transformListToSearchDropdown(security);

  const comfortDropdownChange = useCallback(
    (options: Option | readonly Option[] | null) => {
      let comfort = [...(selected.comfort || [])];

      if (Array.isArray(options)) {
        comfort = options;
      }
      onChange({ ...selected, comfort });
    },
    [selected, onChange]
  );

  const securityDropdownChange = useCallback(
    (options: Option | readonly Option[] | null) => {
      let security = [...(selected.security || [])];

      if (Array.isArray(options)) {
        security = options;
      }
      onChange({ ...selected, security });
    },
    [selected, onChange]
  );

  const entertainmentDropdownChange = useCallback(
    (options: Option | readonly Option[] | null) => {
      let entertainment = [...(selected.entertainment || [])];

      if (Array.isArray(options)) {
        entertainment = options;
      }
      onChange({ ...selected, entertainment });
    },
    [selected, onChange]
  );

  const handleCheck = useCallback(
    (item: string, checked: boolean) => {
      const aircon = [...(selected.aircon || [])];
      if (checked) {
        aircon.push(item);
      } else {
        aircon.splice(aircon.indexOf(item), 1);
      }
      onChange({ ...selected, aircon });
    },
    [selected, onChange]
  );

  return (
    <>
      {aircon && Object.keys(aircon).length > 0 && (
        <>
          <div className={className.subTitleCheckbox}>
            {translate(intl, 'ui.PCP.FILTERS.EQUIPMENT.AIRCON.TITLE')}
          </div>
          <div className={className.elementContentCheckbox}>
            {aircon &&
              aircon.map(
                (airconValue) =>
                  airconValue && (
                    <Checkbox
                      id={`${trackingIdentifierEquipmentAircon}-type-${getSlug(
                        airconValue.name
                      )}`}
                      key={airconValue.name}
                      darkBg={true}
                      label={airconValue.displayName}
                      className={className.airconItem}
                      checked={
                        selected.aircon &&
                        selected.aircon.includes(airconValue.name)
                      }
                      onChange={(v) => handleCheck(airconValue.name, v)}
                    />
                  )
              )}
          </div>
        </>
      )}
      <>
        <div className={className.subTitleDropdown}>
          {translate(intl, 'ui.PCP.FILTERS.EQUIPMENT.COMFORT')}
        </div>
        <div className={className.elementContentDropdown}>
          <SearchDropdown
            id={trackingIdentifierEquipmentComfort}
            placeholder={translate(
              intl,
              'ui.PCP.FILTERS.EQUIPMENT.FILTER_PLACEHOLDER'
            )}
            options={comfortForSearchDropdown}
            darkBg={true}
            isMulti={true}
            hasInfoIcon={false}
            headlinePosition={headlinePosition}
            value={selected.comfort || null}
            onChange={(value: Option | readonly Option[] | null) =>
              comfortDropdownChange(value)
            }
            context="relative-position"
            openHeight={DropdownOpenHeight.MEDIUM}
          ></SearchDropdown>
        </div>
      </>
      <>
        <div className={className.subTitleDropdown}>
          {translate(intl, 'ui.PCP.FILTERS.EQUIPMENT.SECURITY')}
        </div>
        <div className={className.elementContentDropdown}>
          <SearchDropdown
            id={trackingIdentifierEquipmentSecurity}
            placeholder={translate(
              intl,
              'ui.PCP.FILTERS.EQUIPMENT.FILTER_PLACEHOLDER'
            )}
            options={securityForSerachDropdown}
            darkBg={true}
            isMulti={true}
            hasInfoIcon={false}
            headlinePosition={headlinePosition}
            value={selected.security || null}
            onChange={(value: Option | readonly Option[] | null) =>
              securityDropdownChange(value)
            }
            context="relative-position"
            openHeight={DropdownOpenHeight.MEDIUM}
          ></SearchDropdown>
        </div>
      </>
      <>
        <div className={className.subTitleDropdown}>
          {translate(intl, 'ui.PCP.FILTERS.EQUIPMENT.ENTERTAINMENT')}
        </div>
        <div className={className.elementContentDropdown}>
          <SearchDropdown
            id={trackingIdentifierEquipmentEntertainment}
            placeholder={translate(
              intl,
              'ui.PCP.FILTERS.EQUIPMENT.FILTER_PLACEHOLDER'
            )}
            options={entertainmentForSerachDropdown}
            isMulti={true}
            darkBg={true}
            hasInfoIcon={false}
            headlinePosition={headlinePosition}
            value={selected.entertainment || null}
            onChange={(value: Option | readonly Option[] | null) =>
              entertainmentDropdownChange(value)
            }
            context="relative-position"
            openHeight={DropdownOpenHeight.MEDIUM}
          ></SearchDropdown>
        </div>
      </>
    </>
  );
};

export default EquipmentFilter;
